import React from 'react';
import { useI18n } from '../i18n';
import { X } from 'lucide-react';

/**
 * AnimalToggleModal — Logo'ya tıklandığında açılan modal
 *
 * Kullanıcı mod seçer: Kedi | Köpek | Hepsi
 * Seçim ana app state'ine kaydedilir (localStorage veya Context)
 */
export default function AnimalToggleModal({ isOpen, onClose, onSelect, country = 'IN', currentMode = 'cats' }) {
  const { t } = useI18n();

  if (!isOpen) return null;

  // Turkey: 2 option | Other countries: 3 options
  const isTurkey = country === 'TR';
  // FAZ 1E-3: köpek kilidi açıldı. Tür artık yalnız TARAMA süzgeci; kaydedilen
  // türün son kararı sihirbazın son ekranındaki onay rozetinde verilir.
  const options = isTurkey
    ? [
        { value: 'cats', label: t('toggle.cats') || 'Kediler', emoji: '🐱' },
        { value: 'all', label: t('toggle.all') || 'Hepsi', emoji: '🐾' },
      ]
    : [
        { value: 'cats', label: t('toggle.cats') || 'Cats', emoji: '🐱' },
        { value: 'dogs', label: t('toggle.dogs') || 'Dogs', emoji: '🐶' },
        { value: 'all', label: t('toggle.all') || 'All', emoji: '🐾' },
      ];

  const handleSelect = (option) => {
    if (option.yakinda) return; // kilitli — seçilemez
    onSelect(option.value);
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40"
        onClick={onClose}
        style={{ backdropFilter: 'blur(2px)' }}
      />

      {/* Modal */}
      <div className="fixed inset-0 flex items-end z-50">
        <div className="w-full bg-white rounded-t-2xl shadow-2xl p-6 max-h-[50vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-stone-900">
              {t('toggle.selectMode') || 'Hayvan Türü Seç'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
              aria-label="Kapat"
            >
              <X size={20} className="text-stone-600" />
            </button>
          </div>

          {/* Options */}
          <div className="space-y-3">
            {options.map((option) => (
              <button
                key={option.value}
                onClick={() => handleSelect(option)}
                disabled={!!option.yakinda}
                data-testid={`animal-mode-${option.value}`}
                className={`w-full p-4 rounded-xl border-2 transition-all font-semibold text-center flex items-center justify-center gap-3 ${
                  option.yakinda
                    ? 'border-stone-200 bg-stone-50 text-stone-400 cursor-not-allowed'
                    : currentMode === option.value
                    ? 'border-orange-500 bg-orange-50 text-orange-900'
                    : 'border-stone-200 bg-white text-stone-700 hover:border-orange-300 hover:bg-orange-50'
                }`}
              >
                <span className={`text-2xl ${option.yakinda ? 'grayscale opacity-60' : ''}`}>{option.emoji}</span>
                <span>{option.label}</span>
                {option.yakinda && (
                  <span className="text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 rounded-full px-2 py-0.5">
                    {t('toggle.comingSoon') || 'Coming soon'}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Footer hint */}
          <p className="text-xs text-stone-500 mt-6 text-center">
            {t('toggle.hint') || 'Seçtiğin türe göre hayvanlar filtrelenir'}
          </p>
        </div>
      </div>
    </>
  );
}
